import {
  LinkButton,
  Pagination,
  SearchInput,
  Text,
  Tooltip,
} from '$components'
import {
  BinIcon,
  DogIcon,
  EyeIcon,
  PlusIcon,
} from '$components'
import { ElementIntent } from '$components'
import { useDebouncedSearch } from '$hooks'
import { createColumnHelper } from '@tanstack/react-table'
import { ClientCell } from '../components/cells/client-cell'
import { NameCell } from '../components/cells/name-cell'
import { NoPetsEmptyState } from '../components/no-pets-empty-state'
import { PetsFilterParam } from '../enums/filter-params'
import { PageLayout } from 'src/layouts/page'
import { usePage } from '$hooks/use-page'
import { Pet } from '$api-client/types.gen'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { petDeleteMutation, petIndexOptions, petIndexQueryKey } from '$api-client/@tanstack/react-query.gen'
import { AnimatedOutlet } from '$components'
import dayjs from 'dayjs'
import { PetRoutePaths } from '$features/pets/routes/protected'
import { useMemo, useState } from 'react'
import { ConfirmationModal } from '$components'
import { Router } from '$utils/router'
import { useNavigate } from 'react-router'
import { useAuth } from '$features/auth/hooks/use-auth'
import { PermissionsHelper } from '$utils/permissions'
import { Role } from '$api-client/types.gen'
import { GenericTable } from '$components'
import { TextCell } from '$components'
import { OptionsCell } from '$components'
import { useTableSort } from '$hooks/use-table-sort'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { ToastType } from '$components'
import { ToastHelper } from '$components'
import { usePageTitle } from '$components'
import { TableSkeleton } from '$components'

const columnHelper = createColumnHelper<Pet>()

export type DeleteConfirmationModalState = {
  isOpen: boolean,
  petId: number | null,
}

export function PetsPage() {
  usePageTitle('Pets')

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { page, setPage } = usePage()
  const { user } = useAuth()
  const { getSorting } = useTableSort()
  const { hasSubscriptionExpired } = useTenant()

  const [deleteConfirmationModalState, setDeleteConfirmationModalState] = useState<DeleteConfirmationModalState>({
    isOpen: false,
    petId: null,
  })

  const { search, setSearch, debouncedSearch } = useDebouncedSearch({
    searchParam: PetsFilterParam.Search,
    initialValue: '',
    onSearchChange: () => setPage(1),
  })

  const { data: pets, isLoading: isPetsLoading } = useQuery({
    ...petIndexOptions({
      query: {
        query: debouncedSearch,
        page: page,
        sort: getSorting(),
      },
    }),
  })

  const { mutate: deletePet, isPending: isDeletingPet } = useMutation({
    ...petDeleteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: petIndexQueryKey() })
      setDeleteConfirmationModalState({ isOpen: false, petId: null })
      ToastHelper.deleteSuccess(ToastType.Pet)
    },
    onError: () => ToastHelper.deleteError(ToastType.Pet),
  })

  const handleDeletePet = () => {
    deletePet({
      path: {
        pet: deleteConfirmationModalState.petId!,
      },
    })
  }

  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: ({ row }) => (
        <NameCell
          data={{
            name: row.getValue('name'),
            imageUrl: row.original.image,
            fallbackIcon: DogIcon,
          }}
        />
      ),
    }),
    columnHelper.accessor('breed', {
      header: 'Breed',
      cell: ({ row }) => (
        <TextCell>
          {row.getValue('breed')}
        </TextCell>
      ),
    }),
    columnHelper.accessor('birth_date', {
      header: 'Age',
      cell: ({ row }) => (
        <TextCell>
          {dayjs.utc().diff(dayjs.utc(row.getValue('birth_date')), 'years')}
        </TextCell>
      ),
    }),
    columnHelper.accessor('parent', {
      header: 'Parent',
      cell: ({ getValue }) => <ClientCell data={getValue()} />,
    }),
    // columnHelper.accessor('tags', {
    //   header: 'Tags',
    //   cell: ({ getValue }) => <TagsCell tags={getValue()} />,
    // }),
    // columnHelper.accessor('lastAppointment', {
    //   header: 'Last Appointment',
    //   cell: ({ getValue }) => <DateCell date={getValue()} />,
    // }),
    columnHelper.display({
      id: 'actions',
      maxSize: 66,
      minSize: 66,
      cell: ({ row }) => (
        <OptionsCell
          options={[
            {
              id: 'view',
              value: 'View',
              icon: EyeIcon,
              onClick: () => navigate(Router.getRoute(PetRoutePaths.PetsView, { id: row.original.id })),
            },
            {
              id: 'delete',
              value: 'Delete',
              icon: BinIcon,
              intent: ElementIntent.Danger,
              onClick: () => setDeleteConfirmationModalState({ isOpen: true, petId: row.original.id }),
              disabled: PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]),
              tooltip: PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                ? 'You are not allowed to delete pets.'
                : undefined,
            },
          ]}
        />
      ),
    }),
  ]

  const data = useMemo(() => pets?.data ?? [], [pets])

  return (
    <PageLayout>
      {/* Header */}
      <div className="grid grid-cols-1 grid-rows-[auto_1fr] h-full">
        <div className="pb-stack-roomy flex sm:flex-row flex-col sm:items-center justify-between gap-stack-roomy sm:gap-stack-tight">
          <Text
            as="h1"
            weight="medium"
          >
            Pets
          </Text>

          <div className="flex gap-stack-tight items-center">
            <SearchInput
              value={search}
              onChange={setSearch}
              className="w-full sm:w-52"
            />

            <Tooltip
              content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
              disabled={!hasSubscriptionExpired}
            >
              <LinkButton
                to={PetRoutePaths.PetsCreate}
                icon={PlusIcon}
                disabled={hasSubscriptionExpired}
              >
                New Pet
              </LinkButton>
            </Tooltip>
          </div>
        </div>

        {/* Page Content */}
        {isPetsLoading && (
          <TableSkeleton
            columns={5}
            rows={10}
          />
        )}

        {!isPetsLoading && data.length === 0 && (
          <NoPetsEmptyState isNoResults={debouncedSearch.length > 0} />
        )}

        {data.length > 0 && (
          <GenericTable.Container>
            <GenericTable
              data={data}
              columns={columns}
              rowLink={row => Router.getRoute(PetRoutePaths.PetsView, { id: row.id })}
            />

            <Pagination
              currentPage={page}
              onChange={setPage}
              totalPages={pets?.meta.last_page}
              totalItems={pets?.meta.total}
              itemsPerPage={pets?.meta.per_page}
              currentItemCount={pets?.meta.current_page}
            />
          </GenericTable.Container>
        )}
      </div>

      <AnimatedOutlet />

      {deleteConfirmationModalState.isOpen && (
        <ConfirmationModal
          intent={ElementIntent.Danger}
          onClose={() => setDeleteConfirmationModalState({ isOpen: false, petId: null })}
          onConfirm={handleDeletePet}
          isLoading={isDeletingPet}
          title="Delete Pet"
          description="Are you sure you want to delete this pet?"
        />
      )}
    </PageLayout>
  )
}
